import { useEffect, useState } from 'react';
import { Database, Wand2, Copy, Tags, MapPin, Image, Sparkles, ShieldCheck, Rocket, ArrowRight } from 'lucide-react';
import { Card, SectionHeader, Badge, StatusDot } from '@/components/ui';
import { supabase } from '@/lib/supabase';
import { PipelineStage } from '@/lib/types';

const stageMeta: Record<string, { icon: typeof Database; blurb: string; sub: string[] }> = {
  source: {
    icon: Database,
    blurb: 'Discover new records from every enabled connector',
    sub: ['Cron-scheduled workers', 'Retry with backoff', 'Per-source logs'],
  },
  normalization: {
    icon: Wand2,
    blurb: 'Standardize names, phones, emails, addresses',
    sub: ['E.164 phone', 'Postal formatting', 'Trim & casing'],
  },
  duplicate_detection: {
    icon: Copy,
    blurb: 'Compare on name, phone, email, website, geolocation',
    sub: ['Weighted score', 'Configurable threshold', 'Auto-merge or review'],
  },
  ai_categorization: {
    icon: Tags,
    blurb: 'Assign taxonomy from business signals',
    sub: ['Primary + subcategories', 'AI-inferred keywords', 'Confidence scores'],
  },
  address_validation: {
    icon: MapPin,
    blurb: 'Geocode and verify address quality',
    sub: ['Lat/Lng resolution', 'Postal verification', 'City normalization'],
  },
  image_optimization: {
    icon: Image,
    blurb: 'Resize, compress and produce next-gen formats',
    sub: ['WebP + AVIF', 'Thumbnails + blur', 'License-aware'],
  },
  seo_generation: {
    icon: Sparkles,
    blurb: 'Meta, FAQ, schema.org, internal links',
    sub: ['AI summary', 'Auto FAQ', 'JSON-LD schema'],
  },
  admin_review: {
    icon: ShieldCheck,
    blurb: 'Optional human gate for flagged records',
    sub: ['Configurable', 'Auto-skip on high confidence', 'Bulk actions'],
  },
  publish: {
    icon: Rocket,
    blurb: 'Publish, refresh sitemap, notify admins',
    sub: ['Sitemap + RSS', 'Search index update', 'Email + in-app alerts'],
  },
};

export default function Pipeline() {
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    void (async () => {
      const s = await supabase.from('pipeline_stages').select('*').order('position');
      setStages((s.data as PipelineStage[]) ?? []);
      const c: Record<string, number> = {};
      await Promise.all(
        ((s.data as PipelineStage[]) ?? []).map(async (st) => {
          const { count } = await supabase
            .from('listings')
            .select('id', { count: 'exact', head: true })
            .eq('pipeline_stage', st.slug);
          c[st.slug] = count ?? 0;
        }),
      );
      setCounts(c);
    })();
  }, []);

  return (
    <div>
      <SectionHeader
        title="Ingestion Pipeline"
        subtitle="Every imported listing flows through nine stages. Each stage is an independent worker in the queue — retryable, observable and configurable."
      />

      <div className="space-y-4">
        {stages.map((s, i) => {
          const meta = stageMeta[s.slug];
          const Icon = meta?.icon ?? Database;
          const count = counts[s.slug] ?? 0;
          const isReview = s.slug === 'admin_review';
          return (
            <div key={s.id} className="relative">
              <Card className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-4 md:w-72 shrink-0">
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-brand-500/20 to-emerald-500/10 ring-1 ring-brand-400/20 grid place-items-center text-brand-300">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs text-ink-500 font-mono">Stage {i + 1}</div>
                    <div className="text-sm font-semibold text-white flex items-center gap-2">
                      {s.label}
                      {isReview && <Badge tone="warn">configurable</Badge>}
                    </div>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink-200">{meta?.blurb}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {meta?.sub.map((t) => (
                      <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-ink-300 ring-1 ring-white/5">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3 md:w-48 justify-end">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-ink-500">In stage</div>
                    <div className="text-lg font-semibold text-white tabular-nums">{count}</div>
                  </div>
                  <StatusDot tone={count > 0 ? 'info' : 'success'} pulse={count > 0} />
                </div>
              </Card>
              {i < stages.length - 1 && (
                <div className="flex justify-center py-1">
                  <ArrowRight className="h-4 w-4 text-ink-500 rotate-90" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Card className="mt-8 p-5">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-lg bg-emerald-500/10 ring-1 ring-emerald-400/20 grid place-items-center text-emerald-300">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-semibold text-white">Validation pipeline</div>
            <p className="text-xs text-ink-400 mt-1 max-w-3xl">
              Every stage validates its inputs and outputs. Records failing normalization, geocoding, or SEO generation
              are diverted to admin review with a detailed reason attached. Successful records progress automatically —
              no manual intervention required unless auto-publish is disabled in settings.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
